import { FileUploadType } from "elements/Input/FileUpload";
import { PropertyDetails, PropertyDetailsExemptionFields } from ".";

export namespace ExemptionQuestions {
	export const EXEMPTION_QUESTION_IDS = [
		"INTRO",
		"PRIMARY_RESIDENCE",
		"CURRENTLY_RECEIVING",
		"TRANSFERRING_HOMESTEAD",
		"PURCHASE_DATE",
		"MOVE_IN_DATE",
		"RARE_SITUATIONS",
		"PREVIOUS_RESIDENCE",
		"PORTABILITY",
		"US_CITIZEN",
		"HOMESTEAD_QUALIFIED",
		"DOB",
		"SQFT",
		"MARITAL_STATUS",
		"OWNERS",
		"ADDITIONAL_OWNER",
		"SSN_TIN",
		"SSN_TIN2",
		"DL_UPLOAD",
		"DL2_UPLOAD",
		"MANUFACTURED_HOME",
		"OTHER_COUNTIES",
		"OWNERSHIP_PERCENT",
		"NON_AGREEMENT_CONFIRMATION",
		"AGREEMENT",
		"MAIN_CONFIRMATION",
		"TRANSFER_MOVE_SALE_DATE",
		"DRIVERS_LICENSE_NUMBER",
		"DRIVERS_LICENSE_NUMBER2",
		"VEHICLE_REGISTRATIONS",
		"VEHICLE_INFO",
		// "TRANSFER_STAR_CREDIT",
		"TRANSFER_EXEMPTION_REASON",
		"RESIDENCY_PROOF",
		"TAX_FILING_HISTORY",
		"TAX_FILING_HISTORY_DETAILS",
		"TAX_FILING_HISTORY_2",
		"TAX_FILING_HISTORY_DETAILS_2",
		"TAX_FILING_HISTORY_NY_LAST_5_YEARS",
		"TAX_FILING_HISTORY_NY_LAST_5_YEARS_DETAILS",
		"OUT_OF_STATE_TAX_RETURN",
		"TRUST_DOCUMENT_UPLOAD",
		"ANNUAL_HOUSEHOLD_INCOME",
		"PREVIOUS_ADDRESS_SEARCH",
		"SCHOOL_DISTRICT",
		"FARM_LAND",
		"INCOME_PRODUCING",
		"CO_OP",
		"PERCENT_OF_SQUARE_FOOTAGE_USED_AS_BUSINESS_OR_RENTAL_PROPERTY",
		"PERCENT_OF_CO_OP_OWNERSHIP",
		"CURRENT_EMPLOYER",
		"PREVIOUS_TAX_RETURN_ADDRESS",
		"HAS_PORTAL_ACCOUNT",
		"PORTAL_ACCOUNT_CREDENTIALS",
		"NAR_RESOLVED",
		"ALREADY_FILED",
	] as const;

	export type QuestionId = (typeof EXEMPTION_QUESTION_IDS)[number];

	export interface Answers {
		primaryResidenceValue: boolean | undefined;
		currentlyReceivingValue: string | undefined;
		transferringHomesteadValue: boolean | undefined;
		purchaseDateValue: string | undefined;
		moveInDateValue: string | undefined;
		ownerOneValue: number | undefined;
		ownerTwoValue: number | undefined;
		dobValue: string | undefined;
		previousAddressValue: string | undefined;
		previousAddress2Value: string | undefined;
		previousCityValue: string | undefined;
		previousStateValue: string | undefined;
		previousZipcodeValue: string | undefined;
		previousCountyValue: string | undefined;
		previousMoveOutDateValue: string | undefined;
		additionalOwnerFirstNameValue: string | undefined;
		additionalOwnerLastNameValue: string | undefined;
		additionalOwnerEmailValue: string | undefined;
		additionalOwnerPhoneValue: string | undefined;
		additionalOwnerDobValue: string | undefined;
		otherCountiesValue: string | undefined;
		maritalStatusValue: string | undefined;
		ownersValue: string | undefined;
		currentSolarWindExemptionsValue: boolean | undefined;
		installingSolarValue: boolean | undefined;
		owner1SsnTin: string | undefined;
		owner2SsnTin: string | undefined;
		residencyProofFilesToUpload: FileUploadType[];
		trustDocumentFilesToUpload: FileUploadType[];
		ownerDlFilesToUpload: FileUploadType[];
		ownerDlVerificationErrored: boolean | undefined;
		ownerDlExemptValue: boolean | undefined;
		owner2DlFilesToUpload: FileUploadType[];
		owner2DlVerificationErrored: boolean | undefined;
		owner2DlExemptValue: boolean | undefined;
		solarWindInstalled: boolean | undefined;
		otherHeirOwnersOccupy: boolean | undefined;
		isVeteran: boolean | undefined;
		isInheritedProperty: boolean | undefined;
		hasDisability: boolean | undefined;
		survivingSpouseOf65PlusPerson: boolean | undefined;
		disabledVeteranOrSurvivingSpouse: boolean | undefined;
		survivingSpouseOfVeteranKilledInLineOfDuty: boolean | undefined;
		survivingSpouseOfFirstResponderKilledInLineOfDuty: boolean | undefined;
		donatedResidenceOfPartiallyDisabledVetOrSurvivngSpouse: boolean | undefined;
		ownedThroughCooperativeHousing: boolean | undefined;
		ownedThroughNonProfit: boolean | undefined;
		collectRentAtThisProperty: boolean | undefined;
		noneOfTheAbove: boolean | undefined;
		sqFtIncomeProducing: number | undefined;
		manufacturedHomeMake: string | undefined;
		manufacturedHomeModel: string | undefined;
		manufacturedHomeId: string | undefined;
		transferMoveOutDate: string | undefined;
		transferSaleDate: string | undefined;
		transferOwnerDidNotSellProperty: boolean | undefined;
		owner1DlNumberValue: string | undefined;
		owner2DlNumberValue: string | undefined;
		nyGovIdAccountUsername: string | undefined;
		nyGovIdAccountPassword: string | undefined;
		specialFieldsValue:
			| undefined
			| PropertyDetailsExemptionFields["special_fields"];
	}

	export const RARE_SITUATION_KEYS = [
		"is_widow",
		"is_blind",
		"is_disabled_first_responder_or_surviving_spouse_of_a_first_responder",
		"is_surviving_spouse_of_a_first_responder_who_died_in_the_line_of_duty",
		"is_a_disabled_veteran_aged_65_or_older_or_a_surviving_spouse_of_a_disabled_veteran",
		"is_a_disabled_veteran_confined_to_a_wheelchair",
		"is_a_totally_and_permanently_disabled_veteran_or_a_surviving_spouse_of_such_a_veteran",
		"is_a_surviving_spouse_of_a_veteran_who_died_while_on_active_duty",
		"is_65_years_or_older",
		"disabled_veteran",
		"recently_returned_from_active_duty",
		"was_resident_of_assisted_living_facility",
		"is_manufactured_home",
		"surviving_spouse_or_child_of_a_veteran",
		"make_less_than_67k",
		"surviving_spouse_of_a_veteran",
		"is_commercial_property",
		"is_age_18_or_older_disabled_and_under_45k_per_year",
		"is_age_65_or_older_and_under_45k_per_year",
		"is_surving_spouse_of_a_deceased_partner_age_50_or_older",
		"is_active_duty_personnel",
	] as const;

	export type RareSituationKey = (typeof RARE_SITUATION_KEYS)[number];

	export interface FlowContext
		extends Answers,
			Pick<
				PropertyDetails,
				| "use_info"
				| "state"
				| "county"
				| "city"
				| "exemption_requires_both_licenses"
				| "auth_company_type"
			> {
		ownerHasUploadedDlFile: boolean | undefined;
		owner2HasUploadedDlFile: boolean | undefined;
		owner1HasEnteredSsnTin: boolean | undefined;
		owner2HasEnteredSsnTin: boolean | undefined;
		owner1HasEnteredDlNumber: boolean | undefined;
		owner2HasEnteredDlNumber: boolean | undefined;
		futureSavingsAmount: number | undefined;
		retroSavingsAmount: number | undefined;
		exemptionsProgress: number;
		currentMarketValue: number | null;
	}

	export type FlowEvent =
		| { type: "back" }
		| { type: "next" }
		| { type: "" }
		| { type: "restart" }
		| { type: "update_context"; context: Partial<FlowContext> }
		| { type: "ny_tax_nar" }
		| { type: "ny_invalid_credentials_nar" };

	export type FlowTypeState = {
		value: QuestionId;
		context: FlowContext;
	};
}
